// Sidebar → ResolutionEngine `/documents/*` and `/cases/*` calls, as the
// MEDIATOR (Danny, 2026-09-30). The sidebar's own /api/mediator proxy only
// reaches /api/v1/mediator/*, but the /api/engine proxy forwards ANY path
// and passes headers through. The Engine authenticates a Zoom principal when
// it sees `x-keel-zoom-oauth: 1` + the Bearer Zoom token (which it verifies
// with Zoom), so routing doc/case management through /api/engine with those
// headers reaches the same endpoints the desktop editor uses — no new
// backend, and the Engine still enforces auth on every route.

export class SidebarDocError extends Error {
  code?: string;
  status: number;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'SidebarDocError';
    this.status = status;
    this.code = code;
  }
}

async function engineReq<T>(path: string, accessToken: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/engine${path}`, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      authorization: `Bearer ${accessToken}`,
      'x-keel-zoom-oauth': '1',
      'content-type': 'application/json',
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: { code?: string; message?: string } | string };
    const message =
      typeof body.error === 'string'
        ? body.error
        : body.error?.message ?? `Request failed (${res.status})`;
    const code = typeof body.error === 'object' ? body.error?.code : undefined;
    throw new SidebarDocError(message, res.status, code);
  }
  // DELETE / 204 may have no body.
  if (res.status === 204) return {} as T;
  return (await res.json().catch(() => ({}))) as T;
}

export interface DocSigner {
  id: string;
  party_id?: string | null;
  signer_name: string;
  role_label: string | null;
  status: string; // pending | signed | declined
  signer_email?: string | null;
  signer_phone?: string | null;
  sign_url?: string | null;
}

export interface DocSigningStatus {
  agreement_id: string;
  total: number;
  signed: number;
  pending: number;
  declined: number;
  all_signed: boolean;
  signers: DocSigner[];
  agreement_status?: string;
  doc_type?: string;
}

export interface EditableDoc {
  agreement_id: string;
  status: string;
  doc_type?: string;
  body_html: string;
  caption_overrides: Record<string, unknown> | null;
  fields?: { case_no: string; plaintiff: string; defendants: string[]; dated_on: string; division?: string; courtroom?: string };
  prefix_html: string | null;
  suffix_html: string | null;
  /** Signature block rendered from the live signatures rows (appended after
      the body) so the "final version" preview reflects current signer names. */
  signature_html?: string | null;
  styles_css: string;
}

// ── Doc-level signing status + signer CRUD ──────────────────────────────

export function getDocSigningStatus(token: string, agreementId: string): Promise<DocSigningStatus> {
  return engineReq(`/documents/${encodeURIComponent(agreementId)}/signing-status`, token, { method: 'GET' });
}

export function addDocSigner(
  token: string,
  agreementId: string,
  signer: { name: string; role_label?: string; email?: string; phone?: string },
): Promise<{ signer: DocSigner }> {
  return engineReq(`/documents/${encodeURIComponent(agreementId)}/signers`, token, {
    method: 'POST',
    body: JSON.stringify(signer),
  });
}

export function updateDocSigner(
  token: string,
  agreementId: string,
  sigId: string,
  updates: { name?: string; role_label?: string; email?: string; phone?: string },
): Promise<{ signer: DocSigner }> {
  return engineReq(`/documents/${encodeURIComponent(agreementId)}/signers/${encodeURIComponent(sigId)}`, token, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export function removeDocSigner(token: string, agreementId: string, sigId: string): Promise<{ ok: boolean }> {
  return engineReq(`/documents/${encodeURIComponent(agreementId)}/signers/${encodeURIComponent(sigId)}`, token, {
    method: 'DELETE',
  });
}

/** Clear ALL signatures (everyone must re-sign), rotate every link, and
    return the doc to an editable draft. reopenSigned is required to reopen a
    fully-signed document. */
export function cancelSignRequests(
  token: string,
  agreementId: string,
  opts: { reopenSigned?: boolean } = {},
): Promise<{ ok: boolean; status: string; signatures_reset: number }> {
  return engineReq(`/documents/${encodeURIComponent(agreementId)}/cancel-sign-requests`, token, {
    method: 'POST',
    body: JSON.stringify(opts.reopenSigned ? { reopen_signed: true } : {}),
  });
}

// ── Final rendered doc + body edit ──────────────────────────────────────

export function getEditableDoc(token: string, agreementId: string): Promise<EditableDoc> {
  return engineReq(`/documents/${encodeURIComponent(agreementId)}/editable`, token, { method: 'GET' });
}

export function saveDocBody(
  token: string,
  agreementId: string,
  edits: { body_html?: string; caption_overrides?: Record<string, unknown> | null },
): Promise<{ ok: boolean }> {
  return engineReq(`/documents/${encodeURIComponent(agreementId)}`, token, {
    method: 'PATCH',
    body: JSON.stringify({ stipulation: edits }),
  });
}

// ── Authoritative case number / title (case-level) ──────────────────────

export function setCaseNumber(token: string, caseId: string, caseNumber: string): Promise<{ ok: boolean }> {
  return engineReq(`/cases/${encodeURIComponent(caseId)}/case-number`, token, {
    method: 'POST',
    body: JSON.stringify({ case_number: caseNumber }),
  });
}

export function setCaseName(token: string, caseId: string, caseName: string): Promise<{ ok: boolean; case_name: string | null }> {
  return engineReq(`/cases/${encodeURIComponent(caseId)}/case-name`, token, {
    method: 'POST',
    body: JSON.stringify({ case_name: caseName }),
  });
}
